import { useAuthStore } from "store/store"
import styled from "styled-components";
import UserMenu from "./UserMenu";
import { useEffect, useState } from "react";
import axios from "axios";
import { useCartContext } from "context/cart_context";
import moment from "moment";
import FormatPrice from '../Helpers/FormatPrice'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Label,
  LabelList
} from "recharts";

//========================== To add a protected Route that only admins can access this page================
const statusColors = {
  Pending: "#856404",
  ProformaSend:"#9333ea",
  Processing: "#2563eb",
  Shipped: "#eab308",
  Email:"#0d9488",
  Delivered: "#155724",  
  Cancelled: "#dc2626",
};

// ✅ Define CustomLegend here (outside the component)
const CustomLegend = () => {
  return (
    <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
      {Object.keys(statusColors).map((status) => (
        <span key={status} className={`status ${status}`}>
          ● {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ))}
    </div>
  );
};

const UserDashboard=()=>{
    const {auth}=useAuthStore();
    const { cart } = useCartContext();
    const [recentOrders, setRecentOrders] = useState([]);
    const [stats, setStats] = useState({totalOrders: 0,});
    const [statusTotals, setStatusTotals] = useState([]);  //add for chart
   
    useEffect(() => {    
    const getUserStats = async () => {
    try {
      const { data } = await axios.get("/api/orders", {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      if (data.success) {
        const orderTotal = data.orders.reduce(
          (sum, order) => sum + (order.orderTotal || 0),
          0
        );

        setStats({
          totalOrders: data.totalOrders,
          orderTotal, 
        });
    
    // ✅ Aggregate totals by status
          const totalsByStatus = data.orders.reduce((acc, order) => {
            const status = order.status || "Unknown";
            acc[status] = (acc[status] || 0) + (order.orderTotal || 0);
            return acc;
          }, {});

          const chartData = Object.keys(totalsByStatus).map((status) => ({
            status,
            amount: totalsByStatus[status],
          }));

          setStatusTotals(chartData);

     // Recent orders
    const latestOrders = [...data.orders]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 3);

      setRecentOrders(latestOrders);
          }
        } catch (error) {
          console.log(error);
        }
      };

    if (auth?.token) {
      getUserStats();
      }}, 
      [auth?.token]);
      

    return(
      <Wrapper>
        <div className='grid grid-two-column'>
          <UserMenu />            
          <div className="content">
            {/* Welcome Section */}
            <div className="welcome-card">
              <h2>Welcome back </h2>
              <p>Manage your account and track your activity here.</p>
            </div>

            {/* Stats Cards */}
            <div className="stat-grid">
              <div className="stat-card">
                <p>Total Orders</p>
                <h3>{stats.totalOrders}</h3>
              </div>
              <div className="stat-card">
                <p>Wish list</p>
                <h3>5</h3>
              </div>
              <div className="stat-card">
                <p>Cart Items</p>
                <h3>{cart.length}</h3>
              </div>
              <div className="stat-card">
                <p> Order Amount</p>
                <h3><FormatPrice price={stats.orderTotal ? Math.round(stats.orderTotal) : 0} /></h3>
              </div>
            </div>

            {/* Chart with custom legend */}
            <div className="chart-card">
              <h2>Order Status</h2>
              <ResponsiveContainer width="100%" height={350} >
                <BarChart data={statusTotals} 
                   margin={{ top: 20, right: 10, left: 10, bottom: 10 }}>
                  <XAxis dataKey="status">
                    <Label value="Status" 
                    offset={-5} 
                    position="insideBottom"
                    className="axis-label"
                    />
                  </XAxis>
                  <YAxis
                  >
                    <Label value="Amount" 
                    angle={-90} 
                    position="insideLeft"
                    className="axis-label"
                    />
                  </YAxis>
                  <Tooltip formatter={(value) => <FormatPrice price={Math.round(value).toFixed(2)} />} />
                  <Bar dataKey="amount"maxBarSize={80} minPointSize={5}>
                    {statusTotals.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={statusColors[entry.status] || "#8884d8"}
                      />
                    ))}
                    <LabelList 
                      dataKey="amount" 
                      position="top" 
                      formatter={(value) => Number(value).toFixed(2)} 
                    />
                  </Bar>
                 
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Recent Orders */}
            <div className="orders-card">
              <h2>Recent Orders</h2>
                 {recentOrders.length > 0 ? (
                  recentOrders.map((order, index) => (
                <div
                  key={order._id}
                  className={`order-item ${
                  index === recentOrders.length - 1 ? "no-border" : ""
                  }`}
                >
                <div>
                  <p className="order-title">{order.orderNumber}</p>
                  <p className="order-date">
                  {moment(order.createdAt).format("DD MMM YYYY")}
                  </p>
                </div>

                  <span className={`status ${order.status}`}>
                    {order.status}
                  </span>
                </div>
                ))
                  ) : (
                    <p>No orders yet.</p>
                  )}
            </div>
          </div>
        </div> 
      </Wrapper>
    )
}
const Wrapper=styled.div`

.grid-two-column{
  grid-template-columns:2fr 8fr; 
  }

.content{
  padding: 1rem 7rem;
  }

.welcome-card {
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  margin-bottom: 24px;
  }

.welcome-card h2 {
  font-size: 24px;
  margin-bottom: 8px;
  }

.welcome-card p {
  color: #6b7280;
  }

.stat-grid {
  display: flex;                
  justify-content: space-between; 
  align-items: center;        
  margin-bottom: 24px;
  }

.stat-card {
  display: flex;            
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);    
  }

.stat-card p {
  color: #0e62ca;
  margin-bottom: 8px;
  }

.stat-card h3 {
  font-size: 24px;
  font-weight: bold;
  color: #07d162;
  }

.chart-card {
  display: flex;
  flex-direction: column;
  margin: 2rem;
  background: #fff;
  padding: 1rem;
  border-radius: 16px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.3); 
 }

.chart-card h2 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 2rem;
  color: #0e62ca;
  
}
.recharts-cartesian-axis-tick text {
  font-size: 15px;
  fill: #374151;
  font-weight: 400; 
}
  svg {  
  font-size:1.2rem;
  }


.axis-label {
  font-size: 20px;
  font-weight: 600;
  fill: #374151; 
}

/* Tooltip */
.recharts-tooltip-wrapper {
  background: #fff !important;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 13px;
  color: #374151;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}

/* Values above bars */
.recharts-label-list text {
  font-size: 15px;
  font-weight: 600;
   
}

/* Legend */
.recharts-legend-wrapper {
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.recharts-legend-item {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 4px;
  
}
.orders-card {
  background: white;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
 }

.orders-card h2 {
  font-size: 18px;
  margin-bottom: 16px;
 color: #0e62ca;
  }

.order-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
  }

.no-border {
  border-bottom: none;
  margin-bottom: 0;
  }

.order-title {
  font-weight: 500;
  }

.order-date {
  font-size: 14px;
  color: #6b7280;
  }  

.status {
  font-weight: 500;
  font-size: 14px;
  }
.Pending {
  color: #f59e0b;
  }
.ProformaSend {
  color: #0bf1f5;
  }
.Processing {
  color: #10accb;
  }
.Shipped {
  color: #f5f50b;
  }
.Email {
  color: #28e7d3;
  }
.Delivered {
  color: #1ae264;
  }
.Cancelled {
  color: #dc2626;
  }




@media (max-width: ${({ theme }) => theme.media.mobile}) {
    
  .grid-two-column {
    grid-template-columns: 1fr; /* stack into one column */
    }
  .content{
    padding: 1rem;
    }
    
  .welcome-card {
    padding: 1rem; /* reduce padding */
    margin-bottom: 16px;
   }

   
  .welcome-card h2 {
    font-size: 20px; 
    }

  .welcome-card p {
    font-size: 14px; 
    }

  .stat-grid {
    flex-direction: row; 
    gap: 10px; 
    align-items: top;
  }

  .stat-card {
    padding: 10px; 
    }

  .stat-card h3 {
    font-size:15px;
    }

.recharts-label-list text {
  font-size: 10px;
  font-weight: 400;
   
}
    .axis-label {
    font-size: 15px;
    font-weight: 500;
    fill: #374151;  
  }

  .orders-card {
    padding: 16px;
    }

  .orders-card h2 {
    font-size: 16px;
    }

  .order-item {    
    align-items: flex-start;
    }

  .order-title {
    font-size: 14px;
    }

  .order-date, .status {
    font-size: 12px;
    } 
}
`;

export default UserDashboard